/** @jsx jsx */
import React, { useState, useCallback, type ReactNode } from 'react';
import { css, styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Text } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useRequestTypeFieldsLayoutPreference } from '../../../../controllers/request-type-fields-layout-user-preference/index.tsx';
import messages from './messages.tsx';

export const RequestTypeFieldsLayoutPreference = ({
	isRequestTypeSelected,
	overrideIsEnabled = undefined,
	onChange: onChangeCallback,
}: {
	isRequestTypeSelected: boolean;
	overrideIsEnabled?: boolean;
	onChange: () => void;
}) => {
	const { atlassianAccountId } = useTenantContext();
	const [{ data: isEnabled, isLoading }, { setPreference }] =
		useRequestTypeFieldsLayoutPreference();

	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [toggleWasClicked, setToggleWasClicked] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isChecked =
		!toggleWasClicked && overrideIsEnabled !== undefined ? !!overrideIsEnabled : isEnabled;

	const onToggleChanged = useCallback(() => {
		const newValue = !isChecked;
		setToggleWasClicked(true);
		atlassianAccountId &&
			setPreference(atlassianAccountId, newValue, createAnalyticsEvent, onChangeCallback);
	}, [atlassianAccountId, createAnalyticsEvent, isChecked, onChangeCallback, setPreference]);

	const togglePopup = useCallback(() => {
		if (!isOpen) {
			fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeToggleLabelButton clicked');
		}
		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen]);

	const popupContent = useCallback(() => {
		const popupTextRequestTypeSelected = expVal(
			'issue-terminology-refresh-m2-replace',
			'isEnabled',
			false,
		)
			? messages.createFormPopupTextWhenRequestTypeSelectedIssueTermRefresh
			: messages.createFormPopupTextWhenRequestTypeSelected;
		return (
			<PopupContainer>
				<Text as="p" size="UNSAFE_small">
					{formatMessage(
						isRequestTypeSelected
							? popupTextRequestTypeSelected
							: messages.createFormPopupTextWhenNoRequestTypeSelected,
					)}
				</Text>
			</PopupContainer>
		);
	}, [formatMessage, isRequestTypeSelected]);

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		<div css={css({ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' })}>
			<Toggle
				isDisabled={isLoading || !isRequestTypeSelected}
				onChange={onToggleChanged}
				isChecked={isChecked}
				id="use-request-type-fields-toggle"
			/>
			<label
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtlest'),
					font: token('font.body.UNSAFE_small'),
				})}
				htmlFor="use-request-type-fields-toggle"
			>
				{formatMessage(messages.toggleLabel)}
			</label>
			<Popup
				messageId="issue-create.ui.modal.create-form.request-type-fields-layout-preference.popup.jira-popup"
				messageType="engagement"
				placement="right"
				shouldUseCaptureOnOutsideClick
				isOpen={isOpen}
				onClose={togglePopup}
				content={popupContent}
				zIndex={layers.tooltip}
				trigger={(triggerProps) => (
					<div
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={css({
							margin: 0,
							display: 'flex',
							flexDirection: 'row',
							paddingLeft: token('space.050'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
							span: {
								font: token('font.body.small'),
							},
						})}
					>
						<Button {...triggerProps} appearance="subtle-link" spacing="none" onClick={togglePopup}>
							<span
								// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css={css({
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									'-IconAccentColor': token('color.icon.accent.gray'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									'-IconColor': token('color.icon.accent.gray'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									color: isOpen ? 'var(--icon-accent-color)' : 'var(--icon-color)',
									'&:hover': {
										color: 'var(--icon-accent-color)',
									},
								})}
							>
								<EditorPanelIcon label="info" LEGACY_size="small" />
							</span>
						</Button>
					</div>
				)}
			/>
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PopupContainer = styled.div<{
	children?: ReactNode;
}>({
	width: '280px',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});
